// ============================================
// ManualRunner
// Manual test mode: user chats directly with
// the AI Nurse, then clicks End & Analyze to
// score the conversation + run optimizer.
// ============================================

import { useState, useRef, useEffect } from 'react';
import {
  MessageSquare,
  Play,
  Send,
  CheckCircle2,
  XCircle,
  Loader2,
  CircleDot,
  Square,
  Settings,
  DollarSign,
  RotateCcw,
} from 'lucide-react';
import { geminiChat } from '../../../services/ai/providers/gemini';
import {
  buildNurseSystemPromptForTwin,
  buildNurseOpeningInstruction,
  scoreConversation,
  getDefaultPromptRules,
  type ConversationCallbacks,
} from '../../../services/patientJourney/twinEngine';
import { claudeGenerate } from '../../../services/ai/providers/claude';
import { chatWithOptimizer, applyPromptSuggestions } from '../../../services/patientJourney/promptOptimizer';
import { detectConversationModes, generateContextChecks } from '../../../services/patientJourney/contextEngine';
import { saveTestRun } from '../../../services/firestore/pjeService';
import { getActivePromptConfig, savePromptConfig, updatePromptStats } from '../../../services/firestore/promptService';
import type {
  TestPersona,
  Touchpoint,
  Message,
  CheckResult,
  CheckItem,
  ContextLayer,
} from '../../../types/patientJourney';
import { DEFAULT_CONTEXT } from '../../../types/patientJourney';
import type { NursePromptConfig, PromptSuggestion, OptimizerChatMessage } from '../../../types/promptConfig';

// ── Constants ──

const NURSE_MODEL = 'gemini-2.5-flash';
const SCORER_MODEL = 'claude-sonnet-4-5-20250929';

// ── Persona Data ──
// Same persona map as TwinRunner — shared patient data for consistency

function buildContextModifiers(overrides?: Partial<ContextLayer>): ContextLayer {
  return { ...DEFAULT_CONTEXT, ...overrides };
}

function buildDefaultTouchpoint(personaId: string): Touchpoint {
  return {
    touchpointId: `tp-first-${personaId}`,
    type: 'first_contact',
    order: 1,
    label: 'First Contact - Lab Upload',
    trigger: 'Patient uploads lab results for the first time',
    conversationScript: [
      { speaker: 'expected_nurse', text: 'Greet patient and acknowledge lab upload' },
      { speaker: 'patient', text: 'Express concern about results' },
      { speaker: 'expected_nurse', text: 'Provide initial observations and recommendations' },
    ],
    validationChecklist: [],
  };
}

const PERSONA_OPTIONS = [
  { value: 'clean_gate1', label: 'Sarah Mitchell - Clean Gate 1' },
  { value: 'drug_interaction', label: 'Maria Rodriguez - Drug Interaction' },
  { value: 'skeptic', label: 'Dr. Jennifer Walsh - The Skeptic' },
  { value: 'mens_low_t', label: 'James Cooper - Men\'s Low-T' },
  { value: 'mens_polypharm', label: 'Robert Chen - Polypharmacy' },
  { value: 'thyroid_hypo', label: 'Amanda Foster - Hypothyroid' },
  { value: 'metabolic_ir', label: 'David Park - Insulin Resistance' },
  { value: 'fertility_pcos', label: 'Rachel Kim - PCOS / Fertility' },
];

const PERSONA_MAP: Record<string, TestPersona> = {
  clean_gate1: {
    personaId: 'demo-clean-gate1',
    name: 'Sarah Mitchell',
    archetype: 'Clean Gate 1',
    demographics: { name: 'Sarah Mitchell', age: 47, sex: 'Female', state: 'CA' },
    labs: [
      { biomarker: 'Estradiol', value: 38, unit: 'pg/mL', conventionalRange: '12.5-166', functionalRange: '50-100', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Progesterone', value: 1.2, unit: 'ng/mL', conventionalRange: '1.8-24', functionalRange: '10-20', status: 'below_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'DHEA-S', value: 95, unit: '\u00b5g/dL', conventionalRange: '35-430', functionalRange: '150-350', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'FSH', value: 28, unit: 'mIU/mL', conventionalRange: '3.5-12.5', functionalRange: '3.5-8.0', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Cortisol (AM)', value: 22, unit: '\u00b5g/dL', conventionalRange: '6.2-19.4', functionalRange: '10-15', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Perimenopause', 'Fatigue', 'Sleep disruption'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({ motivation: 'concerned', baselineStress: 'moderate' }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'CK-001', check: 'AI recommends specific supplements targeting low DHEA-S (e.g., ashwagandha for adrenal and DHEA-S support)', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'CK-002', check: 'AI recommends adaptogenic herbs like ashwagandha or rhodiola for cortisol/stress support', category: 'clinical', severity: 'major', automatable: true },
      { checkId: 'CK-003', check: 'No prescription medications, HRT, bioidentical hormones, or hormone therapy mentioned — nurse presents three-stage model without using banned words', category: 'compliance', severity: 'critical', automatable: true },
      { checkId: 'CK-004', check: 'Nurse shows empathy for sleep disruption', category: 'conversational', severity: 'major', automatable: true },
    ],
  },
  drug_interaction: {
    personaId: 'demo-drug-interaction',
    name: 'Maria Rodriguez',
    archetype: 'Drug Interactions',
    demographics: { name: 'Maria Rodriguez', age: 52, sex: 'Female', state: 'TX' },
    labs: [
      { biomarker: 'Estradiol', value: 25, unit: 'pg/mL', conventionalRange: '12.5-166', functionalRange: '50-100', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'FSH', value: 45, unit: 'mIU/mL', conventionalRange: '3.5-12.5', functionalRange: '3.5-8.0', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [
      { name: 'Warfarin', dose: '5mg daily', category: 'Blood thinner', duration: '2 years' },
      { name: 'Sertraline', dose: '100mg daily', category: 'SSRI', duration: '3 years' },
    ],
    conditions: ['Postmenopause', 'Atrial fibrillation', 'Depression'],
    contraindications: ['Fish oil (warfarin interaction)', 'St. Johns Wort (SSRI interaction)'],
    allergens: [],
    contextModifiers: buildContextModifiers({ motivation: 'concerned', healthKnowledge: 'basic' }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'CK-010', check: 'Fish oil BLOCKED due to warfarin', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'CK-011', check: 'St. Johns Wort BLOCKED due to SSRI', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'CK-012', check: 'Interaction warnings displayed', category: 'clinical', severity: 'critical', automatable: true },
    ],
  },
  skeptic: {
    personaId: 'demo-skeptic',
    name: 'Dr. Jennifer Walsh',
    archetype: 'The Skeptic',
    demographics: { name: 'Dr. Jennifer Walsh', age: 44, sex: 'Female', state: 'NY' },
    labs: [
      { biomarker: 'Estradiol', value: 42, unit: 'pg/mL', conventionalRange: '12.5-166', functionalRange: '50-100', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'DHEA-S', value: 110, unit: '\u00b5g/dL', conventionalRange: '35-430', functionalRange: '150-350', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Perimenopause'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({ motivation: 'skeptical', healthKnowledge: 'expert', aiComfort: 'distrustful' }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'CK-020', check: 'AI provides evidence citations when challenged', category: 'conversational', severity: 'critical', automatable: true },
      { checkId: 'CK-021', check: 'AI acknowledges limitations honestly', category: 'conversational', severity: 'major', automatable: true },
      { checkId: 'CK-022', check: 'AI maintains composure under pressure', category: 'conversational', severity: 'major', automatable: true },
    ],
  },
  mens_low_t: {
    personaId: 'demo-mens-low-t',
    name: 'James Cooper',
    archetype: 'Clean Gate 1',
    demographics: { name: 'James Cooper', age: 55, sex: 'Male', state: 'FL' },
    labs: [
      { biomarker: 'Total Testosterone', value: 285, unit: 'ng/dL', conventionalRange: '264-916', functionalRange: '500-900', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Free Testosterone', value: 5.2, unit: 'ng/dL', conventionalRange: '5.0-21.0', functionalRange: '10-20', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'DHEA-S', value: 110, unit: '\u00b5g/dL', conventionalRange: '80-560', functionalRange: '200-400', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Low energy', 'Reduced libido', 'Brain fog'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({ motivation: 'concerned', baselineStress: 'moderate' }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'CK-100', check: 'AI recommends DHEA + Zinc protocol', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'CK-101', check: 'AI does not recommend TRT at Gate 1', category: 'compliance', severity: 'critical', automatable: true },
    ],
  },
  mens_polypharm: {
    personaId: 'demo-mens-polypharm',
    name: 'Robert Chen',
    archetype: 'Drug Interactions',
    demographics: { name: 'Robert Chen', age: 61, sex: 'Male', state: 'WA' },
    labs: [
      { biomarker: 'Total Testosterone', value: 220, unit: 'ng/dL', conventionalRange: '264-916', functionalRange: '500-900', status: 'below_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'PSA', value: 3.8, unit: 'ng/mL', conventionalRange: '0-4.0', functionalRange: '0-2.5', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [
      { name: 'Lisinopril', dose: '20mg daily', category: 'ACE inhibitor', duration: '5 years' },
      { name: 'Metformin', dose: '1000mg 2x/day', category: 'Antidiabetic', duration: '3 years' },
    ],
    conditions: ['Hypertension', 'Type 2 Diabetes', 'Low testosterone'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({ motivation: 'concerned', healthKnowledge: 'basic' }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'CK-110', check: 'PSA flagged for medical director review', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'CK-111', check: 'Metformin interaction check performed', category: 'clinical', severity: 'major', automatable: true },
    ],
  },
  thyroid_hypo: {
    personaId: 'demo-thyroid-hypo',
    name: 'Amanda Foster',
    archetype: 'Clean Gate 1',
    demographics: { name: 'Amanda Foster', age: 38, sex: 'Female', state: 'OH' },
    labs: [
      { biomarker: 'TSH', value: 5.8, unit: 'mIU/L', conventionalRange: '0.45-4.5', functionalRange: '1.0-2.5', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Free T4', value: 0.7, unit: 'ng/dL', conventionalRange: '0.82-1.77', functionalRange: '1.0-1.5', status: 'below_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Free T3', value: 2.1, unit: 'pg/mL', conventionalRange: '2.0-4.4', functionalRange: '3.0-4.0', status: 'below_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Hypothyroidism', 'Weight gain', 'Cold intolerance'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({ motivation: 'concerned', baselineStress: 'high' }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'CK-200', check: 'AI recommends selenium + iodine support', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'CK-201', check: 'AI flags for medical director review due to elevated TSH', category: 'compliance', severity: 'critical', automatable: true },
    ],
  },
  metabolic_ir: {
    personaId: 'demo-metabolic-ir',
    name: 'David Park',
    archetype: 'Clean Gate 1',
    demographics: { name: 'David Park', age: 48, sex: 'Male', state: 'CA' },
    labs: [
      { biomarker: 'Fasting Glucose', value: 112, unit: 'mg/dL', conventionalRange: '65-99', functionalRange: '75-90', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'HbA1c', value: 5.9, unit: '%', conventionalRange: '4.0-5.6', functionalRange: '4.5-5.2', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'Fasting Insulin', value: 18, unit: '\u00b5IU/mL', conventionalRange: '2.6-24.9', functionalRange: '3.0-8.0', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['Insulin resistance', 'Pre-diabetes', 'Central obesity'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({ motivation: 'proactive', healthKnowledge: 'informed' }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'CK-300', check: 'AI recommends berberine or chromium protocol', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'CK-301', check: 'AI provides lifestyle modification guidance', category: 'clinical', severity: 'major', automatable: true },
    ],
  },
  fertility_pcos: {
    personaId: 'demo-fertility-pcos',
    name: 'Rachel Kim',
    archetype: 'Clean Gate 1',
    demographics: { name: 'Rachel Kim', age: 32, sex: 'Female', state: 'IL' },
    labs: [
      { biomarker: 'LH', value: 14, unit: 'mIU/mL', conventionalRange: '2.4-12.6', functionalRange: '3.0-8.0', status: 'above_conventional', trigger: true, timestamp: new Date().toISOString() },
      { biomarker: 'FSH', value: 5.0, unit: 'mIU/mL', conventionalRange: '3.5-12.5', functionalRange: '3.5-8.0', status: 'optimal', trigger: false, timestamp: new Date().toISOString() },
      { biomarker: 'DHEA-S', value: 420, unit: '\u00b5g/dL', conventionalRange: '35-430', functionalRange: '150-350', status: 'above_functional', trigger: true, timestamp: new Date().toISOString() },
    ],
    medications: [],
    conditions: ['PCOS suspected', 'Irregular cycles', 'Acne'],
    contraindications: [],
    allergens: [],
    contextModifiers: buildContextModifiers({ motivation: 'desperate', baselineStress: 'high', recentLifeEvent: 'none' }),
    journeyTouchpoints: [],
    validationChecklist: [
      { checkId: 'CK-400', check: 'AI mentions LH:FSH ratio significance', category: 'clinical', severity: 'critical', automatable: true },
      { checkId: 'CK-401', check: 'AI recommends inositol protocol', category: 'clinical', severity: 'major', automatable: true },
    ],
  },
};

// ── Helpers ──

function mergeContextChecks(persona: TestPersona): CheckItem[] {
  const modes = detectConversationModes(persona.contextModifiers);
  const contextChecks = generateContextChecks(modes);
  const existingIds = new Set(persona.validationChecklist.map((c) => c.checkId));
  const newChecks = contextChecks.filter((c) => !existingIds.has(c.checkId));
  return [...persona.validationChecklist, ...newChecks];
}

function getTouchpointForPersona(persona: TestPersona): Touchpoint {
  const mergedChecks = mergeContextChecks(persona);
  if (persona.journeyTouchpoints.length > 0) {
    const tp = { ...persona.journeyTouchpoints[0] };
    tp.validationChecklist = [...tp.validationChecklist, ...mergedChecks.filter(
      (c) => !tp.validationChecklist.some((existing) => existing.checkId === c.checkId),
    )];
    return tp;
  }
  const tp = buildDefaultTouchpoint(persona.personaId);
  tp.validationChecklist = mergedChecks;
  return tp;
}

// ── Component ──

type Phase = 'setup' | 'chatting' | 'scoring' | 'analyzing' | 'done';

export function ManualRunner() {
  const [selectedPersona, setSelectedPersona] = useState('clean_gate1');

  // Conversation state
  const [phase, setPhase] = useState<Phase>('setup');
  const [conversationLog, setConversationLog] = useState<Message[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isNurseTyping, setIsNurseTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gemini history for multi-turn
  const nurseHistoryRef = useRef<{ role: 'user' | 'model'; parts: { text: string }[] }[]>([]);
  const nurseSystemPromptRef = useRef<string>('');

  // Scoring state
  const [checkResults, setCheckResults] = useState<CheckResult[] | null>(null);
  const [passRate, setPassRate] = useState<number | null>(null);
  const [scoringProgress, setScoringProgress] = useState({ current: 0, total: 0 });

  // Optimizer state
  const [activePromptConfig, setActivePromptConfig] = useState<NursePromptConfig | null>(null);
  const [optimizerChat, setOptimizerChat] = useState<OptimizerChatMessage[]>([]);
  const [isChatting, setIsChatting] = useState(false);
  const [optimizerInput, setOptimizerInput] = useState('');
  const [resetStatus, setResetStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');

  // Elapsed timer
  const [elapsed, setElapsed] = useState(0);

  // Load active prompt config on mount
  useEffect(() => {
    getActivePromptConfig().then((config) => {
      if (config) setActivePromptConfig(config);
    }).catch(() => {});
  }, []);

  // Timer while chatting
  useEffect(() => {
    if (phase !== 'chatting' && phase !== 'scoring' && phase !== 'analyzing') return;
    setElapsed(0);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  // Auto-scroll optimizer chat on new messages only
  const optimizerEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    optimizerEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [optimizerChat.length]);

  /** Start the conversation — nurse sends opening message */
  async function handleStart() {
    const persona = PERSONA_MAP[selectedPersona];
    if (!persona) return;

    setError(null);
    setConversationLog([]);
    setCheckResults(null);
    setPassRate(null);
    setOptimizeState();
    setPhase('chatting');
    setIsNurseTyping(true);

    const touchpoint = getTouchpointForPersona(persona);
    const promptTemplate = activePromptConfig?.template || undefined;
    const systemPrompt = buildNurseSystemPromptForTwin(persona, touchpoint, persona.contextModifiers, promptTemplate);
    nurseSystemPromptRef.current = systemPrompt;
    nurseHistoryRef.current = [];

    try {
      const openingInstruction = buildNurseOpeningInstruction(touchpoint);
      const nurseOpening = await geminiChat(
        NURSE_MODEL,
        systemPrompt,
        [],
        openingInstruction,
        0.7,
        2048,
      );

      // Update Gemini history
      nurseHistoryRef.current.push(
        { role: 'user', parts: [{ text: openingInstruction }] },
        { role: 'model', parts: [{ text: nurseOpening }] },
      );

      const tp = getTouchpointForPersona(persona);
      const nurseMsg: Message = {
        role: 'ai_nurse',
        content: nurseOpening,
        timestamp: new Date().toISOString(),
        touchpointId: tp.touchpointId,
      };
      setConversationLog([nurseMsg]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start conversation';
      setError(msg);
      setPhase('setup');
    } finally {
      setIsNurseTyping(false);
    }
  }

  function setOptimizeState() {
    setOptimizerChat([]);
    setOptimizerInput('');
    setScoringProgress({ current: 0, total: 0 });
  }

  /** Send a user message to the nurse */
  async function handleSendMessage() {
    const text = chatInput.trim();
    if (!text || isNurseTyping || phase !== 'chatting') return;

    setChatInput('');

    const persona = PERSONA_MAP[selectedPersona];
    const touchpoint = persona ? getTouchpointForPersona(persona) : null;

    // Add user message
    const userMsg: Message = {
      role: 'patient',
      content: text,
      timestamp: new Date().toISOString(),
      touchpointId: touchpoint?.touchpointId || '',
    };
    setConversationLog((prev) => [...prev, userMsg]);
    setIsNurseTyping(true);

    try {
      const nurseResponse = await geminiChat(
        NURSE_MODEL,
        nurseSystemPromptRef.current,
        nurseHistoryRef.current,
        text,
        0.7,
        2048,
      );

      // Update Gemini history
      nurseHistoryRef.current.push(
        { role: 'user', parts: [{ text }] },
        { role: 'model', parts: [{ text: nurseResponse }] },
      );

      const nurseMsg: Message = {
        role: 'ai_nurse',
        content: nurseResponse,
        timestamp: new Date().toISOString(),
        touchpointId: touchpoint?.touchpointId || '',
      };
      setConversationLog((prev) => [...prev, nurseMsg]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Nurse error';
      setError(msg);
    } finally {
      setIsNurseTyping(false);
    }
  }

  /** End the conversation and run scoring + optimizer */
  async function handleEndAndAnalyze() {
    if (conversationLog.length < 2) {
      setError('Need at least one exchange before analyzing.');
      return;
    }

    setPhase('scoring');
    setError(null);

    const persona = PERSONA_MAP[selectedPersona];
    if (!persona) return;

    const touchpoint = getTouchpointForPersona(persona);
    const checks = touchpoint.validationChecklist;
    setScoringProgress({ current: 0, total: checks.length });

    // Build scoring callbacks
    const callbacks: ConversationCallbacks = {
      callTwin: async () => '', // unused for scoring
      callNurse: async () => '', // unused for scoring
      scoreCheck: async (conversationText, check) => {
        const scoringPrompt = `You are evaluating an AI nurse conversation for quality assurance.

Given the conversation below, determine whether this validation check PASSED or FAILED.

VALIDATION CHECK:
- ID: ${check.checkId}
- Check: ${check.check}
- Category: ${check.category}
- Severity: ${check.severity}

CONVERSATION:
${conversationText}

Respond with EXACTLY this format (no other text):
RESULT: YES or NO
REASONING: Brief 1-2 sentence explanation`;

        const response = await claudeGenerate(
          SCORER_MODEL,
          'You are a clinical conversation quality evaluator. Be objective and strict. Answer only in the required format.',
          scoringPrompt,
          0,
          256,
        );

        const passMatch = response.match(/RESULT:\s*(YES|NO)/i);
        const reasonMatch = response.match(/REASONING:\s*(.+)/is);

        return {
          pass: passMatch ? passMatch[1].toUpperCase() === 'YES' : false,
          reasoning: reasonMatch ? reasonMatch[1].trim() : response.trim(),
        };
      },
      onCheckScored: (result, idx, total) => {
        setScoringProgress({ current: idx + 1, total });
        setCheckResults((prev) => [...(prev || []), result]);
      },
    };

    try {
      const results = await scoreConversation(conversationLog, checks, callbacks);
      const passed = results.filter((r) => r.pass === true).length;
      const rate = results.length > 0 ? Math.round((passed / results.length) * 100) : 0;
      setPassRate(rate);

      // Persist to Firestore
      const runId = `manual_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      try {
        await saveTestRun({
          runId,
          suiteId: 'manual',
          personaId: persona.personaId,
          touchpointId: touchpoint.touchpointId,
          startedAt: new Date(Date.now() - elapsed * 1000).toISOString(),
          completedAt: new Date().toISOString(),
          status: 'completed',
          checkResults: results,
          conversationLog,
          scorer: 'manual',
          passRate: rate,
          suiteVersion: 1,
        });
        if (activePromptConfig) {
          await updatePromptStats(activePromptConfig.versionId, rate).catch(() => {});
        }
      } catch (saveErr) {
        console.warn('Failed to persist manual test run:', saveErr);
      }

      // Auto-start optimizer if not 100%
      if (rate < 100) {
        setPhase('analyzing');
        setIsChatting(true);
        try {
          const currentRules = activePromptConfig?.template || getDefaultPromptRules();
          const runContext = {
            conversationLog,
            checkResults: results,
            validationChecks: checks,
            currentPromptRules: currentRules,
            passRate: rate,
          };
          const { reply, suggestions } = await chatWithOptimizer(
            [],
            'Analyze this manual test run. What failed and why? Suggest specific prompt edits to fix the failures.',
            runContext,
          );
          setOptimizerChat([{
            role: 'optimizer',
            content: reply,
            suggestions: suggestions.length > 0 ? suggestions : undefined,
            timestamp: new Date().toISOString(),
          }]);
        } catch (analyzeErr) {
          console.warn('Failed to analyze run:', analyzeErr);
        } finally {
          setIsChatting(false);
        }
      }

      setPhase('done');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Scoring failed';
      setError(msg);
      setPhase('done');
    }
  }

  /** Reset to setup */
  function handleReset() {
    setPhase('setup');
    setConversationLog([]);
    setChatInput('');
    setCheckResults(null);
    setPassRate(null);
    setError(null);
    setOptimizerChat([]);
    setOptimizerInput('');
    nurseHistoryRef.current = [];
    nurseSystemPromptRef.current = '';
    setScoringProgress({ current: 0, total: 0 });
  }

  // ── Optimizer chat handlers (same as TwinRunner) ──

  function acceptSuggestion(messageIdx: number, suggestionId: string) {
    setOptimizerChat((prev) =>
      prev.map((msg, i) =>
        i === messageIdx && msg.suggestions
          ? { ...msg, suggestions: msg.suggestions.map((s) => s.id === suggestionId ? { ...s, status: 'accepted' as const } : s) }
          : msg,
      ),
    );
  }

  function rejectSuggestion(messageIdx: number, suggestionId: string) {
    setOptimizerChat((prev) =>
      prev.map((msg, i) =>
        i === messageIdx && msg.suggestions
          ? { ...msg, suggestions: msg.suggestions.map((s) => s.id === suggestionId ? { ...s, status: 'rejected' as const } : s) }
          : msg,
      ),
    );
  }

  function getAllAcceptedSuggestions(): PromptSuggestion[] {
    return optimizerChat.flatMap((msg) =>
      (msg.suggestions || []).filter((s) => s.status === 'accepted'),
    );
  }

  async function handleSendOptimizerChat() {
    const text = optimizerInput.trim();
    if (!text || isChatting) return;

    const userMsg: OptimizerChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setOptimizerChat((prev) => [...prev, userMsg]);
    setOptimizerInput('');
    setIsChatting(true);

    try {
      const persona = PERSONA_MAP[selectedPersona];
      const touchpoint = persona ? getTouchpointForPersona(persona) : null;
      const currentRules = activePromptConfig?.template || getDefaultPromptRules();

      const history = optimizerChat.map((m) => ({
        role: (m.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
        content: m.content,
      }));

      const runContext = {
        conversationLog,
        checkResults: checkResults || [],
        validationChecks: touchpoint?.validationChecklist || [],
        currentPromptRules: currentRules,
        passRate: passRate ?? 0,
      };

      const { reply, suggestions } = await chatWithOptimizer(history, text, runContext);

      setOptimizerChat((prev) => [...prev, {
        role: 'optimizer',
        content: reply,
        suggestions: suggestions.length > 0 ? suggestions : undefined,
        timestamp: new Date().toISOString(),
      }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Optimizer error';
      setOptimizerChat((prev) => [
        ...prev,
        { role: 'optimizer', content: `Error: ${msg}`, timestamp: new Date().toISOString() },
      ]);
    } finally {
      setIsChatting(false);
    }
  }

  async function handleApplyAndRerun() {
    const accepted = getAllAcceptedSuggestions();
    if (accepted.length === 0) {
      setError('No accepted suggestions to apply.');
      return;
    }

    setError(null);
    const currentTemplate = activePromptConfig?.template || getDefaultPromptRules();
    const newTemplate = applyPromptSuggestions(currentTemplate, accepted);
    const newVersion = (activePromptConfig?.version || 0) + 1;

    const newConfig: NursePromptConfig = {
      versionId: `prompt_v${newVersion}_${Date.now()}`,
      version: newVersion,
      isActive: true,
      createdAt: new Date().toISOString(),
      source: 'ai_optimizer',
      template: newTemplate,
      changelog: accepted.map((s) => s.reasoning).join('; '),
      stats: { runsCount: 0, avgPassRate: 0, bestPassRate: 0, worstPassRate: 100 },
    };

    try {
      await savePromptConfig(newConfig);
      setActivePromptConfig(newConfig);
      handleReset();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save new prompt version';
      setError(msg);
    }
  }

  /** Reset prompt to the latest built-in default (Trust-First version) */
  async function handleResetToDefault() {
    setResetStatus('saving');
    const newVersion = (activePromptConfig?.version || 0) + 1;
    const newConfig: NursePromptConfig = {
      versionId: `prompt_v${newVersion}_${Date.now()}`,
      version: newVersion,
      isActive: true,
      createdAt: new Date().toISOString(),
      source: 'manual',
      template: getDefaultPromptRules(),
      changelog: 'Reset to built-in Trust-First default prompt',
      stats: { runsCount: 0, avgPassRate: 0, bestPassRate: 0, worstPassRate: 100 },
    };
    try {
      await savePromptConfig(newConfig);
      setActivePromptConfig(newConfig);
      setOptimizerChat([]);
      setResetStatus('done');
      setTimeout(() => setResetStatus('idle'), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset prompt');
      setResetStatus('error');
      setTimeout(() => setResetStatus('idle'), 3000);
    }
  }

  // Suggestion counts
  const allSuggestions = optimizerChat.flatMap((m) => m.suggestions || []);
  const acceptedCount = allSuggestions.filter((s) => s.status === 'accepted').length;
  const pendingCount = allSuggestions.filter((s) => s.status === 'pending').length;
  const hasPending = pendingCount > 0;
  const persona = PERSONA_MAP[selectedPersona];

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)] min-h-[500px]">
      {/* ── Top Bar ── */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl px-5 py-4 space-y-3 flex-shrink-0 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <MessageSquare size={20} className="text-gold" />
            <h3 className="text-lg font-display text-gold">Manual Test</h3>
            <select
              value={selectedPersona}
              onChange={(e) => setSelectedPersona(e.target.value)}
              disabled={phase !== 'setup'}
              className="bg-bg-elevated border border-bg-tertiary rounded-lg px-2.5 py-1.5 text-sm text-text-primary focus:outline-none focus:border-gold/50 appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed max-w-[220px]"
            >
              {PERSONA_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {passRate !== null && (
              <span className={`text-sm font-semibold px-2.5 py-1 rounded-lg ${
                passRate >= 90 ? 'text-emerald-400 bg-emerald-400/10'
                  : passRate >= 70 ? 'text-amber-400 bg-amber-400/10'
                  : 'text-critical bg-critical/10'
              }`}>
                {passRate}%
              </span>
            )}
            {phase !== 'setup' && phase !== 'done' && (
              <span className="text-xs font-mono text-text-muted bg-bg-tertiary px-2 py-0.5 rounded">
                {elapsed}s
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Accept All */}
            {hasPending && (
              <button
                onClick={() => {
                  setOptimizerChat((prev) =>
                    prev.map((msg) =>
                      msg.suggestions
                        ? { ...msg, suggestions: msg.suggestions.map((s) => ({ ...s, status: 'accepted' as const })) }
                        : msg,
                    ),
                  );
                }}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-400/10 text-emerald-400 font-semibold text-sm hover:bg-emerald-400/20 transition-colors border border-emerald-400/30"
              >
                <CheckCircle2 size={14} />
                Accept All ({pendingCount})
              </button>
            )}

            {/* Apply suggestions */}
            {acceptedCount > 0 && (
              <button
                onClick={handleApplyAndRerun}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gold/20 text-gold border border-gold/50 font-bold text-sm hover:bg-gold/30 transition-colors"
              >
                <Play size={14} />
                Apply {acceptedCount} Edits
              </button>
            )}

            {/* Start / End & Analyze / New Test */}
            {phase === 'setup' && (
              <button
                onClick={handleStart}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gold/20 text-gold hover:bg-gold/30 font-semibold text-sm transition-colors"
              >
                <Play size={14} />
                Start Conversation
              </button>
            )}

            {phase === 'chatting' && (
              <button
                onClick={handleEndAndAnalyze}
                disabled={isNurseTyping}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-critical/20 text-critical hover:bg-critical/30 font-semibold text-sm transition-colors border border-critical/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Square size={14} />
                End & Analyze
              </button>
            )}

            {(phase === 'scoring' || phase === 'analyzing') && (
              <span className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-text-muted">
                <Loader2 size={14} className="animate-spin text-gold" />
                {phase === 'scoring'
                  ? `Scoring ${scoringProgress.current}/${scoringProgress.total}`
                  : 'Analyzing...'}
              </span>
            )}

            {phase === 'done' && (
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-secondary hover:text-text-primary hover:border-gold/30 font-semibold text-sm transition-colors"
              >
                <Play size={14} />
                New Test
              </button>
            )}

            <button
              onClick={handleResetToDefault}
              disabled={phase === 'chatting' || phase === 'scoring' || phase === 'analyzing' || resetStatus === 'saving'}
              title="Reset prompt to the built-in Trust-First default"
              className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs transition-colors border disabled:opacity-50 disabled:cursor-not-allowed ${
                resetStatus === 'done'
                  ? 'bg-emerald-400/10 border-emerald-400/30 text-emerald-400'
                  : resetStatus === 'error'
                  ? 'bg-critical/10 border-critical/30 text-critical'
                  : 'bg-bg-elevated border-bg-tertiary text-text-muted hover:text-text-primary hover:border-gold/30'
              }`}
            >
              {resetStatus === 'saving' ? <Loader2 size={12} className="animate-spin" /> : resetStatus === 'done' ? <CheckCircle2 size={12} /> : <RotateCcw size={12} />}
              {resetStatus === 'saving' ? 'Resetting...' : resetStatus === 'done' ? 'Prompt Reset!' : resetStatus === 'error' ? 'Failed' : 'Reset Prompt'}
            </button>
          </div>
        </div>

        {/* Progress bar during scoring */}
        {(phase === 'scoring' || phase === 'analyzing') && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 flex-shrink-0">
              <CircleDot size={12} className="text-gold animate-pulse" />
              <span className="text-xs text-text-primary font-medium">
                {phase === 'scoring' ? 'Scoring checks' : 'Running optimizer'}
              </span>
            </div>
            <div className="flex-1 bg-bg-tertiary rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full bg-gold transition-all duration-500"
                style={{
                  width: phase === 'scoring'
                    ? `${scoringProgress.total > 0 ? (scoringProgress.current / scoringProgress.total) * 90 : 0}%`
                    : '95%',
                }}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="bg-critical/10 border border-critical/20 rounded-lg px-4 py-2.5 flex items-center gap-2">
            <XCircle size={14} className="text-critical flex-shrink-0" />
            <p className="text-sm text-critical">{error}</p>
          </div>
        )}
      </div>

      {/* ── Main: Left sidebar + Right main ── */}
      <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
        {/* ── Left Sidebar: Persona info (setup) or Optimizer Chat (post-analysis) ── */}
        <div className="w-80 flex-shrink-0 flex flex-col overflow-hidden">
          {phase === 'setup' ? (
            <div className="space-y-4 overflow-y-auto flex-1">
              {/* Persona info */}
              {persona && (
                <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
                    <Settings size={14} className="text-gold" />
                    <span className="text-sm font-semibold font-display text-gold">Persona Details</span>
                  </div>
                  <div className="px-4 py-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-text-muted">Name:</span>
                      <span className="text-text-primary font-medium">{persona.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Archetype:</span>
                      <span className="text-text-primary">{persona.archetype}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Age/Sex:</span>
                      <span className="text-text-primary">{persona.demographics.age} / {persona.demographics.sex}</span>
                    </div>
                    <div>
                      <span className="text-text-muted">Conditions:</span>
                      <p className="text-text-primary mt-0.5">{persona.conditions.join(', ')}</p>
                    </div>
                    {persona.medications.length > 0 && (
                      <div>
                        <span className="text-text-muted">Medications:</span>
                        <p className="text-text-primary mt-0.5">{persona.medications.map((m) => m.name).join(', ')}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-text-muted">Triggered Labs:</span>
                      <div className="mt-1 space-y-0.5">
                        {persona.labs.filter((l) => l.trigger).map((l) => (
                          <p key={l.biomarker} className="text-xs text-text-primary">
                            {l.biomarker}: {l.value} {l.unit}
                            <span className={`ml-1 ${l.status.includes('below') ? 'text-amber-400' : l.status.includes('above') ? 'text-critical' : 'text-emerald-400'}`}>
                              ({l.status.replace(/_/g, ' ')})
                            </span>
                          </p>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-text-muted">Checks ({persona.validationChecklist.length}):</span>
                      <div className="mt-1 space-y-0.5">
                        {mergeContextChecks(persona).map((c) => (
                          <p key={c.checkId} className="text-xs text-text-secondary">
                            <span className={`font-mono ${c.severity === 'critical' ? 'text-critical' : 'text-amber-400'}`}>{c.checkId}</span> {c.check}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost */}
              <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
                  <DollarSign size={14} className="text-emerald-400" />
                  <span className="text-sm font-semibold font-display text-gold">Cost</span>
                </div>
                <div className="px-4 py-3 text-xs space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Nurse (Gemini):</span>
                    <span className="text-text-primary">~$0.01/conversation</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Scoring (Claude):</span>
                    <span className="text-text-primary">~$0.02/run</span>
                  </div>
                </div>
              </div>
            </div>
          ) : optimizerChat.length > 0 || phase === 'analyzing' ? (
            /* ── Optimizer Chat Panel ── */
            <div className="flex flex-col flex-1 bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <MessageSquare size={14} className="text-gold" />
                  <span className="text-sm font-semibold font-display text-gold">Optimizer</span>
                </div>
                <div className="flex items-center gap-2">
                  {allSuggestions.length > 0 && (
                    <span className="text-xs text-text-muted">{acceptedCount}/{allSuggestions.length}</span>
                  )}
                  {isChatting && <Loader2 size={12} className="animate-spin text-gold" />}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                {optimizerChat.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Loader2 size={24} className="animate-spin text-gold mb-3" />
                    <p className="text-sm text-text-muted">Analyzing results...</p>
                  </div>
                )}

                {optimizerChat.map((msg, msgIdx) => (
                  <div key={msgIdx}>
                    <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[95%] rounded-xl px-3 py-2.5 ${
                        msg.role === 'user'
                          ? 'bg-bg-elevated border border-bg-tertiary'
                          : 'bg-gold/10 border border-gold/20'
                      }`}>
                        <p className={`text-xs font-semibold mb-1 ${msg.role === 'user' ? 'text-text-secondary' : 'text-gold'}`}>
                          {msg.role === 'user' ? 'You' : 'Optimizer'}
                        </p>
                        <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>

                    {msg.suggestions && msg.suggestions.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {msg.suggestions.map((sug) => (
                          <div
                            key={sug.id}
                            className={`rounded-lg border px-3 py-2.5 ${
                              sug.status === 'accepted' ? 'bg-emerald-400/5 border-emerald-400/30'
                                : sug.status === 'rejected' ? 'bg-critical/5 border-critical/20 opacity-50'
                                : 'bg-bg-elevated border-bg-tertiary'
                            }`}
                          >
                            <p className="text-xs font-semibold text-gold">{sug.section}</p>
                            <p className="text-xs text-text-primary mt-1 leading-relaxed">{sug.reasoning}</p>
                            <p className="text-xs text-text-muted mt-1">Fixes: {sug.addressesChecks.join(', ')}</p>
                            <div className="mt-2">
                              {sug.status === 'pending' && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => acceptSuggestion(msgIdx, sug.id)}
                                    className="px-2.5 py-1 rounded-lg bg-emerald-400/15 text-emerald-400 font-semibold text-xs hover:bg-emerald-400/25 transition-colors border border-emerald-400/30"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => rejectSuggestion(msgIdx, sug.id)}
                                    className="px-2.5 py-1 rounded-lg bg-critical/10 text-critical font-semibold text-xs hover:bg-critical/20 transition-colors border border-critical/30"
                                  >
                                    Reject
                                  </button>
                                </div>
                              )}
                              {sug.status !== 'pending' && (
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                  sug.status === 'accepted' ? 'text-emerald-400 bg-emerald-400/10' : 'text-critical bg-critical/10'
                                }`}>
                                  {sug.status === 'accepted' ? 'ACCEPTED' : 'REJECTED'}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div ref={optimizerEndRef} />
              </div>

              {/* Optimizer input */}
              {optimizerChat.length > 0 && (
                <div className="flex-shrink-0 border-t border-bg-tertiary px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={optimizerInput}
                      onChange={(e) => setOptimizerInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendOptimizerChat(); } }}
                      placeholder="e.g. 'nurse is too wordy'"
                      disabled={isChatting}
                      className="flex-1 bg-bg-elevated border border-bg-tertiary rounded-lg px-2.5 py-2 text-sm text-text-primary placeholder-text-muted/50 focus:outline-none focus:border-gold/50 transition-colors disabled:opacity-50"
                    />
                    <button
                      onClick={handleSendOptimizerChat}
                      disabled={isChatting || !optimizerInput.trim()}
                      className="flex-shrink-0 p-2 rounded-lg bg-gold/20 text-gold hover:bg-gold/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gold/30"
                    >
                      {isChatting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Empty state during chatting/scoring */
            <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
              <MessageSquare size={32} className="text-text-muted/30 mb-3" />
              <p className="text-sm text-text-muted">
                {phase === 'chatting' ? 'Chat with the nurse, then click "End & Analyze"' : 'Scoring in progress...'}
              </p>
            </div>
          )}
        </div>

        {/* ── Right Main Area ── */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Conversation area */}
          <div className="flex-1 bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <MessageSquare size={16} className="text-blue-400" />
                <span className="text-base font-semibold font-display text-gold">Conversation</span>
              </div>
              <span className="text-sm text-text-muted">
                {conversationLog.length > 0 && <>{conversationLog.length} msgs -- {elapsed}s</>}
              </span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {conversationLog.length === 0 && phase === 'setup' && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <MessageSquare size={36} className="text-text-muted/30 mb-3" />
                  <p className="text-base text-text-muted">
                    Select a persona and click "Start Conversation"
                  </p>
                  <p className="text-sm text-text-muted mt-1">
                    You'll chat as the patient, then score the nurse
                  </p>
                </div>
              )}

              {conversationLog.map((msg, idx) => {
                const isNurse = msg.role === 'ai_nurse';
                const turnNumber = Math.floor(idx / 2) + 1;
                return (
                  <div key={idx} className={`flex ${isNurse ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[85%] rounded-xl px-4 py-3 ${
                      isNurse
                        ? 'bg-gold/10 border border-gold/20'
                        : 'bg-bg-elevated border border-bg-tertiary'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold ${isNurse ? 'text-gold' : 'text-text-secondary'}`}>
                          {isNurse ? 'Nurse' : 'You (Patient)'}
                        </span>
                        <span className="text-xs text-text-muted">Turn {turnNumber}</span>
                      </div>
                      <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                );
              })}

              {isNurseTyping && (
                <div className="flex justify-start">
                  <div className="bg-gold/10 border border-gold/20 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin text-gold" />
                      <span className="text-sm text-gold">Nurse is typing...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat input — only during chatting phase */}
            {phase === 'chatting' && (
              <div className="flex-shrink-0 border-t border-bg-tertiary px-5 py-3">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                    placeholder="Type as the patient..."
                    disabled={isNurseTyping}
                    autoFocus
                    className="flex-1 bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-sm text-text-primary placeholder-text-muted/50 focus:outline-none focus:border-gold/50 transition-colors disabled:opacity-50"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={isNurseTyping || !chatInput.trim()}
                    className="flex-shrink-0 p-2.5 rounded-lg bg-gold/20 text-gold hover:bg-gold/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gold/30"
                  >
                    {isNurseTyping ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Validation Results (after scoring) */}
          {checkResults && checkResults.length > 0 && (
            <div className="mt-4 bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400" />
                  <span className="text-base font-semibold font-display text-gold">Validation</span>
                </div>
                {passRate !== null && (
                  <span className={`text-sm font-semibold px-2.5 py-1 rounded-lg ${
                    passRate >= 90 ? 'text-emerald-400 bg-emerald-400/10'
                      : passRate >= 70 ? 'text-amber-400 bg-amber-400/10'
                      : 'text-critical bg-critical/10'
                  }`}>
                    {passRate}% pass rate
                  </span>
                )}
              </div>

              <div className="px-5 py-4 space-y-2">
                {checkResults.map((cr) => {
                  const allChecks = persona ? mergeContextChecks(persona) : [];
                  const checkItem = allChecks.find((c) => c.checkId === cr.checkId);
                  const label = checkItem?.check ?? cr.checkId;

                  return (
                    <div
                      key={cr.checkId}
                      className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
                        cr.pass === true ? 'bg-emerald-400/5 border-emerald-400/20'
                          : cr.pass === false ? 'bg-critical/5 border-critical/20'
                          : 'bg-bg-elevated border-bg-tertiary'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {cr.pass === true ? <CheckCircle2 size={16} className="text-emerald-400" />
                          : cr.pass === false ? <XCircle size={16} className="text-critical" />
                          : <CircleDot size={16} className="text-text-muted" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${
                          cr.pass === true ? 'text-emerald-400'
                            : cr.pass === false ? 'text-critical'
                            : 'text-text-muted'
                        }`}>{label}</p>
                        {cr.notes && <p className="text-xs text-text-muted mt-1 leading-relaxed">{cr.notes}</p>}
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0 ${
                        cr.pass === true ? 'text-emerald-400 bg-emerald-400/10'
                          : cr.pass === false ? 'text-critical bg-critical/10'
                          : 'text-text-muted bg-bg-elevated'
                      }`}>
                        {cr.pass === true ? 'PASS' : cr.pass === false ? 'FAIL' : 'N/A'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
